import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import { characters } from '@/mocks/drama';

const accentMap: Record<string, { chip: string; dot: string; ring: string }> = {
  primary: {
    chip: 'bg-primary-100 text-primary-700',
    dot: 'bg-primary-500',
    ring: 'group-hover:border-primary-300',
  },
  accent: {
    chip: 'bg-accent-100 text-accent-900',
    dot: 'bg-accent-500',
    ring: 'group-hover:border-accent-300',
  },
  secondary: {
    chip: 'bg-secondary-100 text-secondary-800',
    dot: 'bg-secondary-500',
    ring: 'group-hover:border-secondary-300',
  },
};

export default function Characters() {
  const lead = characters.find((item) => item.tier === 'lead');
  const femaleLead = characters.find((item) => item.role === '女主角') ?? lead;

  return (
    <section id="characters" className="scroll-mt-32 bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <Reveal>
          <SectionHeading
            eyebrow="主要角色 · 主演是谁"
            title="角色介绍与主演梳理"
            desc="从上到下依次是贯穿剧情的关键人物。按剧情比重，曼婷是全剧的核心女主角，恩诚则是与她命运紧密相连的男主角。"
          />
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-8 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-vip-crown-2-line" />
              女主角：{femaleLead?.name}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-500 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-user-star-line" />
              男主角：恩诚
            </span>
            <span className="text-xs text-foreground-600 md:text-sm">
              两人共同构成全剧情感与冲突的主线。
            </span>
          </div>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {characters.map((item, index) => {
            const accent = accentMap[item.accent];
            return (
              <Reveal key={item.id} delay={index * 70}>
                <article
                  className={`group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors ${accent.ring}`}
                >
                  <div className="relative h-56 w-full overflow-hidden">
                    <img
                      src={item.img}
                      alt={`${item.name} 角色氛围图`}
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent" />
                    <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-2">
                      <div>
                        <h3 className="font-heading text-xl font-bold text-white">{item.name}</h3>
                        <p className="text-xs text-accent-300">{item.role}</p>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${accent.chip}`}>
                        {item.tier === 'lead' ? '核心主角' : item.tier === 'antagonist' ? '反面势力' : '关键人物'}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col p-5">
                    <p className="text-xs text-foreground-500">{item.position}</p>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.summary}</p>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {item.traits.map((trait) => (
                        <span
                          key={trait}
                          className="inline-flex items-center gap-1.5 rounded-md bg-background-200/80 px-2.5 py-1 text-xs text-foreground-700"
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${accent.dot}`} />
                          {trait}
                        </span>
                      ))}
                    </div>

                    <div className="mt-4 border-t border-background-200 pt-3 text-xs text-foreground-500">
                      <i className="ri-link-m mr-1" />
                      所属故事线：{item.storyline}
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}